/**
 * 최상위 메뉴별 아이콘 이름. 그림 자체는 `components/MenuIcon.tsx` 에 있다.
 *
 * 컴포넌트 파일과 나눠 두는 것은 Fast Refresh 때문이다 —
 * 컴포넌트 파일이 상수까지 내보내면 편집할 때마다 모듈이 통째로 다시 평가된다.
 *
 * <h2>최상위 아홉 줄은 <b>서로 다른 그림</b>이어야 한다 (사용자 지적 2026-09-18)</h2>
 * 여기 적히지 않은 키는 {@link iconNameOf} 의 기본값으로 떨어지는데, 그 값이 달력이던 동안
 * <b>업무 일정·전자결재·자료실·자산관리 넷이 같은 얼굴</b>이었다. 사이드바에서 그림은 글자보다
 * 먼저 읽히는 표지라, 넷이 같으면 표지 노릇을 못 하고 오히려 줄을 헷갈리게 만든다.
 *
 * <p>그래서 <b>최상위는 빠짐없이 여기 적는다</b>({@code ROOT_MENU_ORDER} 의 아홉). 새 최상위를
 * 세우는 날 이 표에 한 줄을 더하지 않으면 그 줄이 다시 기본값과 겹친다.
 */
const ICON_BY_MENU_KEY: Record<string, string> = {
  // 옛 「보미소식」 그룹(`news`)이 쓰던 그림을 그 자리에 선 공지사항이 물려받는다(2026-09-18).
  news_notices: 'message',
  // 게시판은 그때까지 그림이 없어 달력으로 떨어져 있었다. 공지사항 바로 아래에서 달력이 뜨면
  // 업무 일정과 같은 얼굴이 되므로, 글이 쌓이는 자리의 그림을 준다.
  board: 'clipboard',
  site_status: 'layout',
  work: 'calendar',
  // 전자결재 — 승인 표시. 이 메뉴에서 하는 일이 「내 차례를 넘긴다」 하나다.
  approval: 'check',
  // 자료실 — 서류철.
  docs: 'folder',
  // 자산관리 — 상자. 차량·비품·소모품처럼 세어서 관리하는 물건이다.
  assets: 'box',
  // 총무 관리 — 서류가방(사용자 지시 2026-10-22). 이 표에 없어 기본값 격자가 떠 있었다(DOC-120).
  general_affairs: 'briefcase',
  // 원격지원 — 모니터(DOC-137). 남의 PC 화면에 들어가는 자리다.
  remote_support: 'monitor',
  hr: 'users',
  settings: 'settings',
  /*
    보미기술집 — <b>펼친 책</b>(사용자 지시 2026-09-22).

    이 줄은 최상위로 돌아와 있는데(`menuConfig` 의 `groupKey: null`) 이 표에 없어 기본값인
    격자가 뜨고 있었다. 격자는 「아직 정하지 않았다」 는 자리이므로, 아홉 줄 중 하나가 이름표
    없이 서 있던 셈이다.
  */
  tech: 'book',
};

/**
 * 등록되지 않은 메뉴가 기댈 그림. 관리자가 메뉴를 추가해도 아이콘이 비지 않는다.
 *
 * <p><b>진짜 메뉴가 쓰는 그림을 기본값으로 두지 않는다.</b> 예전 기본값은 달력이었는데, 그것은
 * 「업무 일정」의 그림이다 — 사이드바 검색은 <b>하위 줄에도</b> 아이콘을 붙이므로(`Navigation` 의
 * `renderSearchResult`), 검색 결과가 온통 업무 일정의 얼굴을 하고 있었다. 격자는 어느 메뉴의
 * 것도 아니라서 「아직 정하지 않았다」로 읽힌다.
 */
const FALLBACK_ICON = 'grid';

export function iconNameOf(menuKey: string): string {
  return ICON_BY_MENU_KEY[menuKey] ?? FALLBACK_ICON;
}
